import React, { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import PaymentRow, { PAYMENT_STATUSES } from '@/components/PaymentRow';
import TableStateBody from '@/components/TableStateBody';
import PageHeader from '@/components/PageHeader';
import TableFilters from '@/components/TableFilters';
import useFetchList from '@/hooks/useFetchList';

const PAYMENT_COLUMNS = 8;

function matchesSearch(payment, query) {
  if (!query) return true;
  const s = query.toLowerCase();
  const haystack = `${payment.id} ${payment.booking_id} ${payment.stripe_payment_intent_id || ''}`.toLowerCase();
  return haystack.includes(s);
}

export default function Payments() {
  const {
    data: payments,
    loading,
    refresh: fetchPayments,
  } = useFetchList('/admin/payments', { errorLabel: 'payments' });

  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () =>
      payments.filter((p) => {
        if (statusFilter !== 'all' && p.status !== statusFilter) return false;
        return matchesSearch(p, search);
      }),
    [payments, statusFilter, search],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Payments"
        subtitle="Read-only ledger of all booking transactions"
        onRefresh={fetchPayments}
        loading={loading}
        refreshTestId="btn-refresh-payments"
      />

      <Card className="p-4 border-slate-200">
        <TableFilters
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by payment ID, booking ID or Stripe ID"
          searchTestId="payments-search"
          statusValue={statusFilter}
          onStatusChange={setStatusFilter}
          statuses={PAYMENT_STATUSES}
          statusTestId="payments-status-filter"
          statusLabel={(s) => s}
        />

        <div className="rounded-lg border border-slate-200 overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Payment</TableHead>
                <TableHead>Booking</TableHead>
                <TableHead>Stripe PI</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Commission</TableHead>
                <TableHead>Payout</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableStateBody
                loading={loading}
                isEmpty={filtered.length === 0}
                colSpan={PAYMENT_COLUMNS}
                emptyText="No payments found"
              >
                {filtered.map((p) => (
                  <PaymentRow key={p.id} payment={p} />
                ))}
              </TableStateBody>
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
